import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  MAX_PERSONALIZATION_FILE_BYTES,
  PERSONALIZATION_FILES,
  matchLineEndings,
  parseDisplayName,
  personalizationByteLength,
  personalizationFailureText,
  parsePersonalizationSnapshot,
  withDisplayName,
  toLF,
  type PersonalizationFileName,
  type PersonalizationSnapshot,
} from '../components/settings/personalization';
import { makeDefaultKeyFor } from '../config';
import { useTerminalConfig } from '../context/config-context';
import { createSafeContext } from '../context/createSafeContext';
import { SIDECAR_CODES, SidecarError } from '../sidecar/request';
import { useAgentActions, useAgentState } from './contexts';

/**
 * The persona files and the editing session over them, shared by the Settings
 * pane (display name) and the Personalization page (the editor).
 *
 * One provider, mounted once: both surfaces write USER.md, so they must see
 * one snapshot and one set of drafts, or a save from one silently reverts the
 * other. Data comes from the gateway pod's sidecar over HTTP
 * (`AgentActions.sidecarRequest`: the host's web-api forward on the cloud
 * path, the gateway origin with the stored token otherwise):
 *
 *   GET  /personalization/v1/state                  → { files, unreadable }
 *   POST /personalization/v1/file {file, content}   → { files, unreadable }
 *
 * The socket still gates the reads: `authenticated` is what says the gateway
 * is resolved and its credential seeded, so loading and reconnect states are
 * the socket's.
 */

const STATE_PATH = '/personalization/v1/state';
const FILE_PATH = '/personalization/v1/file';

/**
 * `unavailable` is permanent — what answered has no such feature — and swaps
 * the editor out. Every other failure is `error`: stated, retryable, and the
 * pane keeps what it has.
 */
export type PersonalizationStatus =
  | 'loading'
  | 'ready'
  | 'error'
  | 'unavailable';

export type FileDrafts = Partial<Record<PersonalizationFileName, string>>;

export interface PersonalizationApi {
  readonly status: PersonalizationStatus;
  readonly snapshot: PersonalizationSnapshot | null;
  /** The last read failure, shown above a pane that may still be usable. */
  readonly error: string | null;
  /** USER.md line 1. */
  readonly displayName: string;
  readonly reload: () => Promise<void>;
  readonly saveFile: (
    file: PersonalizationFileName,
    content: string,
  ) => Promise<void>;
  readonly saveDisplayName: (name: string) => Promise<void>;

  /** The editor session: drafts held per file across tabs and surfaces. */
  readonly editing: boolean;
  readonly saving: boolean;
  readonly drafts: FileDrafts;
  /** Files whose draft differs from the stored file. */
  readonly dirtyFiles: readonly PersonalizationFileName[];
  readonly startEdit: () => void;
  readonly discardEdit: () => void;
  readonly setDraft: (file: PersonalizationFileName, value: string) => void;
  /**
   * Write every draft not yet in browser storage, now. Called where the text
   * leaves the user's sight (a tab switch, the page hiding), since the
   * keystroke path only writes once per interval.
   */
  readonly flushDrafts: () => void;
  /** Writes every dirty file. Rejects with the first failure. */
  readonly saveDrafts: () => Promise<void>;
}

const [usePersonalization, Provider] = createSafeContext<PersonalizationApi>(
  'PersonalizationContext',
);
export { usePersonalization };

/**
 * Only two answers mean the feature is absent, as opposed to failing: there
 * is no sidecar to ask (a gateway URL nothing can be derived from and no
 * forward configured), or a 404 — from the web-api in front of us or the
 * sidecar itself, either of which then predates personalization. A 5xx, a
 * 429, a refused credential or a network failure are all a gateway that may
 * answer next time.
 */
function isMissingFeature(err: unknown): boolean {
  if (!(err instanceof SidecarError)) return false;
  return (
    err.status === 404 ||
    (err.status === 0 && err.code === SIDECAR_CODES.NO_SIDECAR)
  );
}

/**
 * A write the agent refused or could not complete, worded for the pane and
 * carrying the sidecar's code for anything that wants to distinguish.
 */
export class PersonalizationSaveError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(source: SidecarError) {
    super(personalizationFailureText(source, 'write'));
    this.name = 'PersonalizationSaveError';
    this.status = source.status;
    this.code = source.code;
  }
}

/** A refusal, not a failure: the content is over the per-file cap. */
export class PersonalizationTooLargeError extends Error {
  constructor(readonly file: PersonalizationFileName) {
    super(`${file} is too large to save. Trim it and try again.`);
    this.name = 'PersonalizationTooLargeError';
  }
}

/** What a failed read says: the sidecar table for its failures, else the error's own words. */
function readFailureText(err: unknown): string {
  if (err instanceof SidecarError)
    return personalizationFailureText(err, 'read');
  if (err instanceof Error && err.message.length > 0) return err.message;
  return "Couldn't reach your personalization. Try again in a moment.";
}

// ── Drafts in browser storage ───────────────────────────────────────────────
//
// A page refresh must not eat an editing session, so every draft is mirrored
// to localStorage as it is typed, keyed per account and per file, together
// with the server content it was typed against. On load it is restored only
// while that base is still what the server holds: a file another device saved
// meanwhile shows as the server has it, and the stale draft is dropped rather
// than laid over it.

/** The `keyFor` scope. The default shape puts it under `terminal_…`. */
const DRAFT_SCOPE = 'personalizationDrafts';

/** How often a changed file is written to storage while it is being typed. */
export const DRAFT_PERSIST_INTERVAL_MS = 5_000;

interface StoredDraft {
  /** The server content this draft was typed against. */
  readonly base: string;
  readonly draft: string;
}

/** Storage can be absent, sandboxed or full; none of that is the pane's. */
function readStoredDraft(key: string): StoredDraft | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return null;
    const { base, draft } = parsed as Record<string, unknown>;
    if (typeof base !== 'string' || typeof draft !== 'string') return null;
    return { base, draft };
  } catch {
    return null;
  }
}

function writeStoredDraft(key: string, value: StoredDraft | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Nothing to do: the in-memory session is unaffected.
  }
}

export function PersonalizationProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  const { sidecarRequest } = useAgentActions();
  const { status: connectionStatus } = useAgentState();
  const {
    onUpdateProfile,
    logException,
    accountId,
    namespace,
    keyFor: hostKeyFor,
  } = useTerminalConfig();
  const isReady = connectionStatus === 'authenticated';

  const [status, setStatus] = useState<PersonalizationStatus>('loading');
  const [snapshot, setSnapshot] = useState<PersonalizationSnapshot | null>(
    null,
  );
  // Mirrors `snapshot` synchronously, for the save path: what a write left
  // on the server is needed before React has rendered it.
  const snapshotRef = useRef<PersonalizationSnapshot | null>(null);
  const commitSnapshot = useCallback(
    (
      next: (
        prev: PersonalizationSnapshot | null,
      ) => PersonalizationSnapshot | null,
    ) => {
      snapshotRef.current = next(snapshotRef.current);
      setSnapshot(snapshotRef.current);
    },
    [],
  );
  const [error, setError] = useState<string | null>(null);
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // A read that started before the latest write or read is dropped whole:
  // a write's response is the newest truth, and an older read would revert it.
  const seq = useRef(0);

  const reload = useCallback(async (): Promise<void> => {
    if (!isReady) {
      // Not connected is not news about the gateway; a loaded pane stays as
      // it is. A read still in flight is retired with the socket: landing it
      // would present a live pane over a dead connection.
      seq.current += 1;
      setStatus((prev) =>
        prev === 'ready' || prev === 'unavailable' ? prev : 'loading',
      );
      return;
    }
    const mine = ++seq.current;
    try {
      const payload = await sidecarRequest(STATE_PATH);
      if (mine !== seq.current) return;
      const parsed = parsePersonalizationSnapshot(payload);
      commitSnapshot(() => parsed);
      setStatus(parsed === null ? 'unavailable' : 'ready');
      setError(null);
    } catch (err) {
      if (mine !== seq.current) return;
      if (isMissingFeature(err)) {
        commitSnapshot(() => null);
        setStatus('unavailable');
        setError(null);
        return;
      }
      // A gateway already proven to lack the feature stays unavailable.
      if (statusRef.current === 'unavailable') return;
      setError(readFailureText(err));
      setStatus((prev) => (prev === 'ready' ? prev : 'error'));
    }
  }, [isReady, sidecarRequest, commitSnapshot]);

  // Re-read for a new account as well as a new connection: the account reset
  // below empties the snapshot, and nothing else asks for it again.
  useEffect(() => {
    void reload();
  }, [reload, accountId]);

  const saveFile = useCallback(
    async (file: PersonalizationFileName, content: string): Promise<void> => {
      if (personalizationByteLength(content) > MAX_PERSONALIZATION_FILE_BYTES) {
        // Re-checked at the write: the textarea stays live during a save.
        throw new PersonalizationTooLargeError(file);
      }
      const account = accountRef.current;
      let payload: unknown;
      try {
        payload = await sidecarRequest(FILE_PATH, {
          method: 'POST',
          body: { file, content },
        });
      } catch (err) {
        // The sidecar's code becomes the pane's sentence here, once, rather
        // than in each surface that renders a save failure.
        if (err instanceof SidecarError)
          throw new PersonalizationSaveError(err);
        throw err;
      }
      // The write went to the account that issued it; if the account changed
      // while it was in flight, its answer is not this session's snapshot and
      // its name line is not this account's profile.
      if (accountRef.current !== account) return;
      seq.current += 1;
      setError(null);
      const before = snapshotRef.current?.files[file] ?? '';
      const parsed = parsePersonalizationSnapshot(payload);
      commitSnapshot((prev) => {
        if (parsed !== null) return parsed;
        return prev === null
          ? prev
          : { ...prev, files: { ...prev.files, [file]: content } };
      });

      // USER.md is the source of truth for the display name; the host copy
      // (Clerk, on the cloud path) follows whichever surface CHANGED it. A
      // name line the agent wrote is not pushed to the profile by a save that
      // left it alone, and a deleted name line means "edit my file", not
      // "blank my account".
      if (file !== 'USER.md' || typeof onUpdateProfile !== 'function') return;
      const name = parseDisplayName(content);
      if (name === '' || name === parseDisplayName(before)) return;
      try {
        await onUpdateProfile({ displayName: name });
      } catch (err) {
        logException?.(err, { where: 'personalization:mirrorDisplayName' });
      }
    },
    [sidecarRequest, onUpdateProfile, logException, commitSnapshot],
  );

  const saveDisplayName = useCallback(
    async (name: string): Promise<void> => {
      const userMd = snapshot?.files['USER.md'] ?? '';
      await saveFile('USER.md', withDisplayName(userMd, name));
    },
    [snapshot, saveFile],
  );

  const displayName = useMemo(
    () => parseDisplayName(snapshot?.files['USER.md'] ?? ''),
    [snapshot],
  );

  // ── Editor session ──────────────────────────────────────────────────────

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState<FileDrafts>({});
  // The textarea stays live during a save, so the post-save "still dirty?"
  // check must read the drafts as they are now, not as rendered at the click.
  const draftsRef = useRef<FileDrafts>({});
  const editDrafts = useCallback((next: (prev: FileDrafts) => FileDrafts) => {
    draftsRef.current = next(draftsRef.current);
    setDrafts(draftsRef.current);
  }, []);

  // Where each file's draft lives across a refresh. Scoped by the account the
  // rest of the package's storage is scoped by (the host's account id; the
  // IdP user id on the cloud path) — signed out, there is nowhere to put it.
  const draftKey = useMemo(() => {
    if (accountId === null) return null;
    const keyFor = hostKeyFor ?? makeDefaultKeyFor(namespace);
    return (file: PersonalizationFileName): string =>
      keyFor(DRAFT_SCOPE, accountId, file);
  }, [accountId, namespace, hostKeyFor]);
  // The server content this session's drafts were typed against: set when
  // the session starts (or is restored), moved forward for a file a save
  // wrote. A snapshot refresh mid-session does not move it — a draft is not
  // "based on" content the user never saw.
  const baseRef = useRef<FileDrafts>({});
  // One file's draft as storage should hold it now: the current draft against
  // its base, or nothing when they agree (the textarea is LF; the base may
  // not be).
  const writeDraft = useCallback(
    (file: PersonalizationFileName) => {
      if (draftKey === null) return;
      const value = draftsRef.current[file];
      const base = baseRef.current[file] ?? '';
      writeStoredDraft(
        draftKey(file),
        value === undefined || value === toLF(base)
          ? null
          : { base, draft: value },
      );
    },
    [draftKey],
  );
  // Keystrokes do not each write: a file can be tens of kilobytes, and
  // serialising it into storage on every character makes typing drag. A
  // changed file is written at most once per interval (the first change
  // writes at once, so a crash shortly after still loses little) and always
  // when the text leaves the user's sight — `flushDrafts` on a tab switch or
  // the page hiding. Anything that empties storage cancels the pending write
  // too, or a discard would come back on the next refresh.
  const pendingRef = useRef<Set<PersonalizationFileName>>(new Set());
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPersistRef = useRef(0);
  const cancelPendingPersist = useCallback(() => {
    if (persistTimerRef.current !== null) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    pendingRef.current.clear();
  }, []);
  const flushDrafts = useCallback(() => {
    const pending = [...pendingRef.current];
    cancelPendingPersist();
    if (pending.length === 0) return;
    for (const file of pending) writeDraft(file);
    lastPersistRef.current = Date.now();
  }, [cancelPendingPersist, writeDraft]);
  const scheduleDraftPersist = useCallback(
    (file: PersonalizationFileName) => {
      pendingRef.current.add(file);
      if (persistTimerRef.current !== null) return;
      const wait =
        DRAFT_PERSIST_INTERVAL_MS - (Date.now() - lastPersistRef.current);
      if (wait <= 0) {
        flushDrafts();
        return;
      }
      persistTimerRef.current = setTimeout(() => {
        persistTimerRef.current = null;
        flushDrafts();
      }, wait);
    },
    [flushDrafts],
  );
  const clearStoredDrafts = useCallback(() => {
    cancelPendingPersist();
    if (draftKey === null) return;
    for (const file of PERSONALIZATION_FILES)
      writeStoredDraft(draftKey(file), null);
  }, [cancelPendingPersist, draftKey]);
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushDrafts();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flushDrafts);
    window.addEventListener('beforeunload', flushDrafts);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flushDrafts);
      window.removeEventListener('beforeunload', flushDrafts);
      // Unmounting is the text leaving sight too; a pending write must
      // neither be lost nor fire after the provider is gone.
      flushDrafts();
    };
  }, [flushDrafts]);

  const dirtyFiles = useMemo(
    () =>
      PERSONALIZATION_FILES.filter((file) => {
        if (snapshot?.unreadable.includes(file)) return false;
        const value = drafts[file];
        return value != null && value !== toLF(snapshot?.files[file] ?? '');
      }),
    [drafts, snapshot],
  );

  const startEdit = useCallback(() => {
    baseRef.current = { ...snapshotRef.current?.files };
    editDrafts(() => ({}));
    clearStoredDrafts();
    setEditing(true);
  }, [editDrafts, clearStoredDrafts]);

  const discardEdit = useCallback(() => {
    editDrafts(() => ({}));
    clearStoredDrafts();
    setEditing(false);
  }, [editDrafts, clearStoredDrafts]);

  const setDraft = useCallback(
    (file: PersonalizationFileName, value: string) => {
      editDrafts((prev) => ({ ...prev, [file]: value }));
      scheduleDraftPersist(file);
    },
    [editDrafts, scheduleDraftPersist],
  );

  // ── The account ─────────────────────────────────────────────────────────
  //
  // Everything above is one account's: the snapshot is its agent's files, the
  // drafts are edits to them, and a save writes them to its agent. Nothing
  // else keys on the account (the socket re-authenticates on its own time),
  // so a switch is handled here, during render as PreferencesProvider does —
  // one render later and the new account's tree has already seen the old
  // account's state. In-flight reads and writes are retired: `seq` retires
  // the read, `accountRef` retires the write's answer. The persisted drafts
  // are keyed by account and are not touched — they are restored for the
  // account they belong to when its snapshot lands.
  const [loadedAccount, setLoadedAccount] = useState(accountId);
  const accountRef = useRef(accountId);
  if (accountId !== loadedAccount) {
    setLoadedAccount(accountId);
    accountRef.current = accountId;
    seq.current += 1;
    snapshotRef.current = null;
    draftsRef.current = {};
    baseRef.current = {};
    setSnapshot(null);
    setStatus('loading');
    setError(null);
    setDrafts({});
    setEditing(false);
    setSaving(false);
  }

  // Restore a stored session once the server's files are known, and only
  // while there is no live session to collide with. A stored draft whose base
  // is no longer what the server holds was overtaken by a save elsewhere and
  // is dropped; one equal to the server content is nothing to restore.
  useEffect(() => {
    if (snapshot === null || editing || draftKey === null) return;
    const restored: FileDrafts = {};
    for (const file of PERSONALIZATION_FILES) {
      const key = draftKey(file);
      const stored = readStoredDraft(key);
      if (stored === null) continue;
      const server = snapshot.files[file];
      if (
        stored.base !== server ||
        stored.draft === toLF(server) ||
        snapshot.unreadable.includes(file)
      ) {
        writeStoredDraft(key, null);
        continue;
      }
      restored[file] = stored.draft;
    }
    if (Object.keys(restored).length === 0) return;
    baseRef.current = { ...snapshot.files };
    editDrafts(() => restored);
    setEditing(true);
  }, [snapshot, editing, draftKey, editDrafts]);

  const saveDrafts = useCallback(async (): Promise<void> => {
    if (!editing || saving) return;
    const account = accountRef.current;
    setSaving(true);
    try {
      const written: FileDrafts = {};
      for (const file of dirtyFiles) {
        const draft = draftsRef.current[file];
        if (draft === undefined) continue;
        const value = matchLineEndings(snapshot?.files[file] ?? '', draft);
        await saveFile(file, value);
        // The session this loop belonged to was reset by an account switch.
        if (accountRef.current !== account) return;
        // As typed (LF): the post-save comparison is against the textarea.
        written[file] = draft;
        // A draft typed during the write is now based on what the write left.
        baseRef.current[file] = snapshotRef.current?.files[file] ?? value;
      }
      // Keep only drafts that still differ from what is stored: a draft typed
      // and reverted would otherwise outlive the session and hold USER.md.
      editDrafts((prev) => {
        const next: FileDrafts = { ...prev };
        for (const file of PERSONALIZATION_FILES) {
          const stored = written[file] ?? toLF(snapshot?.files[file] ?? '');
          if (next[file] === stored) delete next[file];
        }
        return next;
      });
      const stillDirty = PERSONALIZATION_FILES.some((file) => {
        if (snapshot?.unreadable.includes(file)) return false;
        const value = draftsRef.current[file];
        return (
          value !== undefined &&
          value !== (written[file] ?? toLF(snapshot?.files[file] ?? ''))
        );
      });
      if (stillDirty) {
        // The bases moved; what storage holds for every file is stale.
        for (const file of PERSONALIZATION_FILES) pendingRef.current.add(file);
        flushDrafts();
      } else {
        // The session is over, and nothing it held survives it — including
        // a draft of a file that became unreadable, which no save can write
        // and which would otherwise hold USER.md against the Settings row.
        editDrafts(() => ({}));
        clearStoredDrafts();
      }
      setEditing(stillDirty);
    } finally {
      setSaving(false);
    }
  }, [
    editing,
    saving,
    dirtyFiles,
    saveFile,
    editDrafts,
    snapshot,
    flushDrafts,
    clearStoredDrafts,
  ]);

  const api = useMemo<PersonalizationApi>(
    () => ({
      status,
      snapshot,
      error,
      displayName,
      reload,
      saveFile,
      saveDisplayName,
      editing,
      saving,
      drafts,
      dirtyFiles,
      startEdit,
      discardEdit,
      setDraft,
      flushDrafts,
      saveDrafts,
    }),
    [
      status,
      snapshot,
      error,
      displayName,
      reload,
      saveFile,
      saveDisplayName,
      editing,
      saving,
      drafts,
      dirtyFiles,
      startEdit,
      discardEdit,
      setDraft,
      flushDrafts,
      saveDrafts,
    ],
  );

  return <Provider value={api}>{children}</Provider>;
}
